import React from 'react';
import { FiltersParams, TagCategoryParams } from 'types/params';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { t, Trans } from '@lingui/macro';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import {
    Box,
    Checkbox,
    useBreakpoint,
    Text,
    Input,
    SearchOutlined,
} from '@landr/maestro';
import { noop } from 'utils/noop';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { FirstLevelItemObject } from 'components/Filters/context/FiltersContext/types';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { useFilterState } from '../../FilterPanes/components/useFilterState';
import { useSortingTag } from '../hooks/useSortingTag';

interface TagFilterProps {
    filterType: FiltersParams;
    activeFilters: string[];
    tags: FirstLevelItemObject[];
    totalNumberOfResults: number;
    addFilter: (modifiers: ModifiersPayload[]) => void;
    removeFilter: (modifiers: ModifiersPayload[]) => void;
    tagsCategory: TagCategoryParams;
}

const TagFilter: React.FC<TagFilterProps> = ({
    filterType,
    activeFilters,
    tags,
    totalNumberOfResults,
    addFilter,
    removeFilter,
    tagsCategory,
}) => {
    const { isFilterChecked, isFilterDisabled, toggleFilter } = useFilterState({
        filterType,
        activeFilters,
        totalNumberOfResults,
        addFilter,
        removeFilter,
    });
    const { getFilterLabel } = useApiFiltersContext();
    const [searchValue, setSearchValue] = React.useState('');

    const smallDesktop = useBreakpoint('lg');

    const { groupedTags } = useSortingTag({
        tags,
        searchValue,
        tagsCategory,
        filterType,
    });

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
    };

    return (
        <Box display="flex" flexDirection="column" gap="lg">
            {tagsCategory === TagCategoryParams.artist && (
                <Input
                    placeholder={t`Search an artist...`}
                    width="100%"
                    value={searchValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={(e) => {
                        // Prevent sample playback when input is focused
                        e.stopPropagation();
                    }}
                    marginBottom="md"
                    marginTop="sm"
                    variant="filled"
                    isRounded={true}
                    icon={<SearchOutlined />}
                />
            )}

            {Object.keys(groupedTags).length === 0 && (
                <Text>
                    <Trans>
                        No results found. Try using different keyword.
                    </Trans>
                </Text>
            )}

            {Object.entries(groupedTags).map(([groupKey, groupTags]) => (
                <Box
                    key={groupKey}
                    display="flex"
                    flexDirection="column"
                    gap="md"
                    paddingBottom="md"
                >
                    <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="text.medium"
                    >
                        {groupKey}
                    </Text>
                    <Box
                        display="grid"
                        gridTemplateColumns={
                            !smallDesktop ? '1fr 1fr 1fr 1fr' : '1fr 1fr'
                        }
                        gridAutoFlow="xs"
                        columnGap="sm"
                        rowGap="sm"
                    >
                        {groupTags.map((tag) => {
                            if (!tag) {
                                return null;
                            }

                            const isChecked = isFilterChecked(tag.code);
                            const label =
                                getFilterLabel(tag.code, filterType)
                                    .charAt(0)
                                    .toUpperCase() +
                                getFilterLabel(tag.code, filterType).slice(1);

                            return (
                                <Checkbox
                                    key={tag.code}
                                    size="sm"
                                    label={label}
                                    isDisabled={isFilterDisabled(tag)}
                                    isChecked={isChecked}
                                    onClick={() => toggleFilter(tag.code)}
                                    onChange={noop}
                                    py="2xs"
                                />
                            );
                        })}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export const TagsFiltersContent: React.FC<{
    filterType: TagCategoryParams;
    isMobile?: boolean;
    tags: FirstLevelItemObject[];
    activeTags: FirstLevelItemObject[];
    tagsCategory: TagCategoryParams;
}> = ({ isMobile, tags, activeTags, tagsCategory }) => {
    const { addFilter, removeFilter } = useSearchModifiers();
    const { activeTabTotal } = useSearchController();

    return (
        <Box height="100%" position="relative" width="100%">
            <Box
                paddingX={isMobile ? 'sm' : '2xl'}
                width="100%"
                position="relative"
                paddingBottom="140px"
                display="flex"
                flexDirection="column"
            >
                <TagFilter
                    filterType={FiltersParams.tags}
                    activeFilters={activeTags?.map((tag) => tag.code) ?? []}
                    tags={tags ?? []}
                    totalNumberOfResults={activeTabTotal ?? 0}
                    addFilter={addFilter}
                    removeFilter={removeFilter}
                    tagsCategory={tagsCategory}
                />
            </Box>
        </Box>
    );
};

export default TagsFiltersContent;
